import React from 'react';
import get from 'lodash/get';
import { useRecoilState } from 'recoil';
import { useDebounce, useUnmount } from 'react-use';
import { DataTable } from 'app/components/Charts/table';
import { PageLoader } from 'app/modules/common/page-loader';
import { useStoreActions, useStoreState } from 'app/state/store/hooks';
import { downloadActivitiesCSV } from 'app/utils/downloadActivitiesCSV';
import { getAPIFormattedFilters } from 'app/utils/getAPIFormattedFilters';
import { DetailProjectsFragmentModel } from 'app/fragments/detail-projects-fragment/data';
import {
  selectedFilterAtom,
  lineYearPeriodFilter,
} from 'app/state/recoil/atoms';
import {
  ActivitiesDataTableColumns,
  ActivitiesDataTableOptions,
} from 'app/components/Charts/table/data';

export function DetailProjectsFragment(props: DetailProjectsFragmentModel) {
  const [search, setSearch] = React.useState<undefined | string>();
  const [debouncedSearch, setDebouncedSearch] = React.useState<
    undefined | string
  >();
  const [tablePage, setTablePage] = React.useState(0);
  const [tableRows, setTableRows] = React.useState(10);
  const [tableSortBy, setTableSortBy] = React.useState<{
    name: string;
    direction: 'asc' | 'desc';
  }>({
    name: 'Planned start date',
    direction: 'desc',
  });

  const action = useStoreActions((actions) => actions.detailActivities.fetch);
  const actionData = useStoreState((actions) => actions.detailActivities.data);
  const clearAction = useStoreActions(
    (actions) => actions.detailActivities.clear
  );

  const tableData = get(actionData, 'data', []);
  const tableCount = get(actionData, 'count', []);

  const [yearPeriod] = useRecoilState(lineYearPeriodFilter);
  const [selectedFilters] = useRecoilState(selectedFilterAtom);

  function callAction(page: number, rows: number, sort: string, param: string) {
    const filters = getAPIFormattedFilters(
      selectedFilters,
      props.includeYearPeriodFilter ? yearPeriod : undefined
    );
    action({
      values: {
        filters: {
          ...filters,
          [props.detailBaseFilter]: [param],
        },
        page,
        rows,
        sort,
        search: debouncedSearch,
      },
    });
  }

  React.useEffect(() => {
    callAction(
      tablePage,
      tableRows,
      `${tableSortBy.name} ${tableSortBy.direction}`,
      props.pageParam
    );
  }, [
    tablePage,
    tableRows,
    tableSortBy,
    props.pageParam,
    selectedFilters,
    debouncedSearch,
  ]);

  useDebounce(
    () => {
      setDebouncedSearch(search);
    },
    500,
    [search]
  );

  useUnmount(() => clearAction());

  const [loading] = React.useState(false);

  return (
    <React.Fragment>
      {loading && <PageLoader />}
      <DataTable
        data={tableData}
        columns={ActivitiesDataTableColumns}
        options={{
          ...ActivitiesDataTableOptions,
          serverSide: true,
          onSearchChange: (searchText) =>
            searchText != null && setSearch(searchText),
          onSearchClose: () => {
            setSearch(undefined);
            setDebouncedSearch(undefined);
          },
          onDownload: () => {
            const filters = getAPIFormattedFilters(selectedFilters);
            downloadActivitiesCSV(
              {
                ...filters,
                [props.detailBaseFilter]: [props.pageParam],
              },
              `${tableSortBy.name} ${tableSortBy.direction}`,
              debouncedSearch,
              tableCount
            );
            return false;
          },
          searchPlaceholder: 'Search activities',
          sortOrder: tableSortBy,
          count: tableCount,
          onChangePage: setTablePage,
          onChangeRowsPerPage: setTableRows,
          onColumnSortChange: (
            changedColumn: string,
            direction: 'asc' | 'desc'
          ) =>
            setTableSortBy({
              name: changedColumn,
              direction,
            }),
        }}
        title={props.pageTitle}
      />
    </React.Fragment>
  );
}
